'use client';

/**
 * DirectMessageBadge — sidebar badge with unread DM count.
 *
 * Spec ref: Sprint 134 (Founder directive 2026-09-19) — chat entre
 *           patrocinador y directos. Sprint 135 añadió stickers +
 *           archivos adjuntos. Sprint 135 (Founder directive 2026-09-22):
 *           añadir indicador visual en el sidebar cuando hay mensajes
 *           directos sin leer (WhatsApp-style).
 *
 * Mirrors `NotificationBadge` but for direct messages:
 *   - Polls `GET /api/direct-messages/unread/count` every 30s (lightweight
 *     — only fetches the count, not the messages). The endpoint + the
 *     `api.directMessages.getUnreadCount()` typed wrapper already exist
 *     (see `api-client.ts` line ~8404 + backend
 *     `direct-message.controller.ts:getUnread`).
 *   - Listens to the `direct-message:new` WebSocket event (emitted by
 *     `direct-message.service.ts:sendDirectMessage` to the `user:<toUserId>`
 *     room — the recipient's socket). On event, increments the count
 *     immediately (no need to wait for the next 30s poll).
 *   - Refetches the count when the user navigates to `/app/network/directos*`
 *     because the backend marks messages as read on conversation fetch
 *     (`getConversation` → `DirectMessage.updateMany({ readAt: new Date() })`).
 *
 * Variants (same as NotificationBadge):
 *   variant='count' (default) — small red badge with the unread count,
 *                               shown next to the "Mis Directos" item.
 *   variant='dot'              — small solid red dot (no number), shown
 *                               next to the "Mi Red" group label header
 *                               so the user sees an indicator WITHOUT
 *                               expanding the group (WhatsApp-style).
 *
 * §6 Anti-Invención: the backend endpoint already exists — this component
 *   only READS the count. No business logic on the frontend.
 * Anti-P-041: 0 `as any` casts.
 * Anti-P-042: 0 `console.*` calls.
 * Anti-P-043: AbortController for in-flight fetch cancellation on unmount.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { Badge } from '@/components/ui/badge';
import { api, ApiClientError } from '@/lib/api-client';
import { useSocket } from '@/hooks/use-socket';

export interface DirectMessageBadgeProps {
  /**
   * 'count' (default) — renders a small red badge with the unread count.
   * 'dot' — renders a small solid red dot (no number). Used on the
   * "Mi Red" PARENT group label so the user sees a visual indicator
   * without expanding the group (WhatsApp-style).
   */
  variant?: 'count' | 'dot';
}

export function DirectMessageBadge({ variant = 'count' }: DirectMessageBadgeProps) {
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const pathname = usePathname();
  // Destructure `on` + `isConnected` from useSocket so the WS listener
  // useEffect can depend on those stable references directly (avoids the
  // `react-hooks/exhaustive-deps` warning that fires when the whole
  // `socket` object — recreated every render — is omitted from the dep
  // array). `on` is a useCallback inside useSocket with `[isConnected]`
  // as its dep, so it only changes when the socket (re)connects.
  const { on: socketOn, isConnected: socketIsConnected } = useSocket();
  // AbortController ref — cancels the in-flight fetch on unmount / re-fetch.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch the unread count from the backend. Lightweight — only the count.
  // Silent-fail: the badge is non-critical; on 401 (logged out) we reset
  // to 0 (matches the NotificationBadge pattern).
  const fetchCount = useCallback(async (): Promise<void> => {
    // Cancel any previous in-flight request before starting a new one.
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const res = await api.directMessages.getUnreadCount(controller.signal);
      setUnreadCount(res.count);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        // User logged out — don't show error, just set count to 0.
        setUnreadCount(0);
      }
      // Other errors (network, 500, AbortError) — leave the previous
      // count as-is. The badge is non-critical; the next 30s poll will
      // retry.
    }
  }, []);

  // Initial fetch + polling (every 30s — matches NotificationBadge cadence).
  useEffect(() => {
    void fetchCount();
    const interval = setInterval(() => {
      void fetchCount();
    }, 30_000);
    return () => {
      abortControllerRef.current?.abort();
      clearInterval(interval);
    };
  }, [fetchCount]);

  // Refetch when navigating to /app/network/directos (the conversations
  // page) — the backend's `getConversation` marks messages as read on
  // fetch, so the count drops when the user opens a conversation. Without
  // this refetch, the badge would show a stale (higher) count until the
  // next 30s poll.
  useEffect(() => {
    if (pathname.startsWith('/app/network/directos')) {
      void fetchCount();
    }
  }, [pathname, fetchCount]);

  // WebSocket listener for `direct-message:new` events. The backend
  // emits this to `user:<toUserId>` (the recipient's room — see
  // `direct-message.service.ts:sendDirectMessage` line ~285). The
  // `useSocket` hook auto-joins the user's room via the backend
  // `socket-auth.middleware.ts`, so any new message addressed to the
  // current user fires this event. We increment the count immediately
  // (no need to wait for the next 30s poll).
  //
  // Deps: `[socketOn, socketIsConnected]` — `socketOn` is a useCallback
  // that changes when `socketIsConnected` flips (see use-socket.ts line
  // ~361), so this effect re-subscribes only when the socket
  // (re)connects, NOT on every render.
  useEffect(() => {
    if (!socketIsConnected) return;
    const unsubscribe = socketOn('direct-message:new', (...args: unknown[]) => {
      // The backend emits the message payload (DirectMessageDTO shape).
      // We don't need to inspect it — any new message addressed to the
      // current user increments the unread count (the backend's
      // `io.to(`user:${toUserId}`).emit(...)` scopes the event to the
      // recipient; the sender's socket does NOT receive it).
      void args;
      setUnreadCount((prev) => prev + 1);
    });
    return unsubscribe;
  }, [socketOn, socketIsConnected]);

  // Don't render the badge if unreadCount is 0 (clean UI when no new DMs)
  if (unreadCount === 0) return null;

  if (variant === 'dot') {
    // Small solid red dot — used on the "Mi Red" PARENT group label so
    // the user sees an indicator WITHOUT expanding the group. Matches
    // the NotificationBadge dot variant for visual consistency.
    return (
      <span
        aria-label={`${unreadCount} mensajes sin leer`}
        className="inline-block size-2 shrink-0 rounded-full bg-destructive"
      />
    );
  }

  return (
    <Badge
      variant="destructive"
      className="ml-auto h-5 px-1.5 text-xs min-w-5 flex items-center justify-center"
      aria-label={`${unreadCount} mensajes sin leer`}
    >
      {unreadCount > 99 ? '99+' : unreadCount}
    </Badge>
  );
}
